import React from 'react';
import Logger from '../../../shared/Logger';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Button,
  Flex,
  Label,
  Input,
  Select,
  Checkbox,
  FormMessage,
  Text,
  Toast,
  theme,
} from '@xometry/xometry_loft';
import ReactSelect from 'react-select';
import countryListUtil from '@xometry/npm_package_country_list';
import {
  city,
  name,
  state,
  street1,
  street2,
} from '../../../utils/validations';
import { provinces, states } from '../../../utils/constants';
import { ShippingAddressPanel } from '../ShippingAddress.styles';
import {
  EditShippingAddressFormProps,
  entityTypeEnum,
  MappedShippingAddress,
} from '../types';
import { validatePhoneNumber, validateEmail } from '../../../utils/validators';

const stateOptions = Object.keys(states).map((key) => (
  <option key={key} value={states[key]}>
    {states[key]}
  </option>
));

const provinceOptions = Object.keys(provinces).map((key) => (
  <option key={key} value={provinces[key as keyof typeof provinces]}>
    {provinces[key as keyof typeof provinces]}
  </option>
));

export const shippingAddressSchema = yup.object().shape({
  name: name
    .required('Full Name is required')
    .max(70, 'Full name must be no more than 70 characters'),
  street1: street1
    .required('Street address line 1 is required')
    .max(35, 'Street address line 1 must be no more than 35 characters'),
  street2: street2
    .nullable()
    .max(35, 'Street address line 2 must be no more than 35 characters'),
  street3: yup
    .string()
    .nullable()
    .max(35, 'Street address line 3 must be no more than 35 characters'),
  countryCode: yup.string().required('Country is required'),
  city: city
    .required('City is required')
    .max(35, 'City must be no more than 35 characters'),
  state: state.when('countryCode', {
    is: (countryCode: string) => countryCode === 'US',
    then: state
      .required('State is required when country is USA')
      .test(
        'valid-us-state',
        'Invalid state. Please provide a valid US state.',
        (value: any) => {
          if (!value) return true; // Let required validation handle empty values
          // Validate against both state names and codes
          const validStateValues = Object.values(states); // ['AL', 'AK', etc.]
          const validStateNames = Object.keys(states); // ['Alabama', 'Alaska', etc.]
          return (
            validStateValues.includes(value) || validStateNames.includes(value)
          );
        }
      ),
    otherwise: state.nullable(),
  }),
  province: yup.string().when('countryCode', {
    is: (countryCode: string) => countryCode === 'CA',
    then: yup
      .string()
      .required('Province is required when country is Canada')
      .test(
        'valid-ca-province',
        'Invalid province. Please provide a valid Canadian province.',
        (value: any) => {
          if (!value) return true; // Let required validation handle empty values
          // Validate against both province names and codes
          const validProvinceValues = Object.values(provinces); // ['NL', 'PE', etc.]
          const validProvinceNames = Object.keys(provinces); // ['Newfoundland and Labrador', etc.]
          return (
            validProvinceValues.includes(value) ||
            validProvinceNames.includes(value)
          );
        }
      ),
    otherwise: yup.string().nullable(),
  }),
  zipcode: yup
    .string()
    .max(10, 'Zip Code must be no more than 10 characters')
    .required('Zip Code or Postal Code is Required'),
  phoneNumber: yup
    .string()
    .required('Phone Number is Required')
    .test(
      'valid-phone',
      'Phone number is invalid',
      (value: any, context: any) => {
        if (!value) return true; // Let required validation handle empty values

        const result = validatePhoneNumber(value, context.parent.countryCode);
        if (!result.isValid) {
          return context.createError({
            message: result.errorMessage || 'Phone number is invalid',
          });
        }
        return true;
      }
    ),
  phoneExt: yup
    .string()
    .nullable()
    .max(6, 'Phone extension must be no more than 6 characters'),
  emailAddress: yup
    .string()
    .required('Email is Required')
    .max(80, 'Email address must be no more than 80 characters')
    .test('valid-email', 'Email is invalid', (value: any) => {
      if (!value) return true; // Let required validation handle empty values
      return validateEmail(value);
    }),
  internationalVerification: yup.boolean(),
});

const EditShippingAddressForm = (props: EditShippingAddressFormProps) => {
  const {
    onCancelClick,
    onSave,
    onRemove,
    requestFailed,
    shippingAddress,
    showMode,
    index,
    entityType,
  } = props;

  const isEdit = showMode === 'Edit';

  // APP-57638: we're using State to store Province for teams
  let provinceValue;
  if (isEdit && shippingAddress) {
    if (shippingAddress.countryCode === 'CA') {
      if (entityType === entityTypeEnum.user) {
        provinceValue = shippingAddress.province;
      } else {
        provinceValue = shippingAddress.state;
      }
    }
  } else {
    provinceValue = '';
  }

  const isFirstTeamAddress = index === 0;
  const initValues = {
    id: isEdit && shippingAddress ? shippingAddress.id : null,
    name: isEdit && shippingAddress ? shippingAddress.name : '',
    street1: isEdit && shippingAddress ? shippingAddress.street1 : '',
    street2: isEdit && shippingAddress ? shippingAddress.street2 : '',
    street3: isEdit && shippingAddress ? shippingAddress.street3 : '',
    country: isEdit && shippingAddress ? shippingAddress.country : '',
    countryCode: isEdit && shippingAddress ? shippingAddress.countryCode : '',
    city: isEdit && shippingAddress ? shippingAddress.city : '',
    state: isEdit && shippingAddress ? shippingAddress.state : '',
    province: provinceValue,
    zipcode: isEdit && shippingAddress ? shippingAddress.zipcode : '',
    phoneNumber: isEdit && shippingAddress ? shippingAddress.phoneNumber : '',
    phoneExt: isEdit && shippingAddress ? shippingAddress.phoneExt : '',
    emailAddress: isEdit && shippingAddress ? shippingAddress.emailAddress : '',
    internationalVerification: false,
    defaultIndicator: isEdit
      ? shippingAddress?.defaultIndicator
      : isFirstTeamAddress,
    validated: false,
    validationExempt: false,
  };

  // This form is used by Team and User
  // Only add street3 and phone_ext when form is used for User
  if (entityType !== entityTypeEnum.user) {
    delete initValues['street3'];
    delete initValues['phoneExt'];
  }

  return (
    <ShippingAddressPanel kind="primary">
      <Formik
        initialValues={initValues}
        onSubmit={(values, { setSubmitting }) => {
          return onSave(values as MappedShippingAddress)
            .catch((error) => {
              Logger.error('Error saving shipping address', error, {
                component: 'EditShippingAddressForm',
                action: 'saveAddress',
              });
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
        validationSchema={shippingAddressSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
        }) => {
          const invalidFullName = Boolean(errors.name && touched.name);
          const invalidStreetName = Boolean(errors.street1 && touched.street1);
          const invalidStreet2 = Boolean(errors.street2 && touched.street2);
          const invalidStreet3 = Boolean(errors.street3 && touched.street3);
          const invalidCountry = Boolean(
            errors.countryCode && touched.countryCode
          );
          const invalidCity = Boolean(errors.city && touched.city);
          const invalidState = Boolean(errors.state && touched.state);
          const invalidProvince = Boolean(errors.province && touched.province);
          const invalidZipCode = Boolean(errors.zipcode && touched.zipcode);
          const invalidPhoneNumber = Boolean(
            errors.phoneNumber && touched.phoneNumber
          );
          const invalidPhoneExt = Boolean(errors.phoneExt && touched.phoneExt);
          const invalidEmailAdress = Boolean(
            errors.emailAddress && touched.emailAddress
          );

          const stateSelect = (
            <Box paddingLeft={theme.space[2]}>
              <Label htmlFor="state" marginBottom={theme.space[1]}>
                State
              </Label>
              <Select
                className="pii-encrypt"
                id="state"
                aria-label="Select a state"
                backgroundColor={theme.colors.white}
                value={values.state}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={values.countryCode !== 'US'}
              >
                <option disabled hidden value="">
                  Select...
                </option>
                {stateOptions}
              </Select>
              {invalidState && (
                <FormMessage invalid={invalidState}>{errors.state}</FormMessage>
              )}
            </Box>
          );

          const provinceSelect = (
            <Box paddingLeft={theme.space[2]}>
              <Label htmlFor="province" marginBottom={theme.space[1]}>
                Province
              </Label>
              <Select
                className="pii-encrypt"
                id="province"
                backgroundColor={theme.colors.white}
                value={values.province}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={values.countryCode !== 'CA'}
              >
                <option disabled hidden value="">
                  Select...
                </option>
                {provinceOptions}
              </Select>
              {invalidProvince && (
                <FormMessage invalid={invalidProvince}>
                  {errors.province}
                </FormMessage>
              )}
            </Box>
          );

          // There isn't a class for the div on line 199 due to IE issues with display:flex
          return (
            <form onSubmit={handleSubmit}>
              <Box maxWidth="324px">
                <Flex flexDirection="column">
                  <Text
                    kind="body-title"
                    fontWeight={theme.fontWeights.bold}
                    marginBottom={theme.space[3]}
                  >
                    {showMode} Shipping Address
                  </Text>
                  <Flex marginBottom={theme.space[2]} alignItems="center">
                    <Checkbox
                      id="defaultIndicator"
                      checked={values.defaultIndicator}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={index === 0}
                    />
                    <Label
                      paddingLeft={theme.space[1]}
                      htmlFor="defaultIndicator"
                      scale="large"
                      lineHeight={theme.lineHeights[3]}
                      fontWeight={theme.fontWeights.regular}
                      marginBottom={theme.space[0]}
                    >
                      Make this my default shipping address
                    </Label>
                  </Flex>
                  <Box marginBottom={theme.space[5]}>
                    <Label htmlFor="name" marginBottom={theme.space[1]}>
                      Full Name
                    </Label>
                    <Input
                      className="pii-encrypt"
                      id="name"
                      width="100%"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Full Name"
                      invalid={invalidFullName}
                    />
                    {invalidFullName && (
                      <FormMessage invalid={invalidFullName}>
                        {errors.name}
                      </FormMessage>
                    )}
                  </Box>
                  <Box marginBottom={theme.space[5]}>
                    <Label htmlFor="street1" marginBottom={theme.space[1]}>
                      Street Address
                    </Label>
                    <Input
                      className="pii-encrypt"
                      width="100%"
                      marginBottom={theme.space[2]}
                      id="street1"
                      value={values.street1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Address line 1"
                      invalid={invalidStreetName}
                    />
                    {invalidStreetName && (
                      <FormMessage invalid={invalidStreetName}>
                        {errors.street1}
                      </FormMessage>
                    )}
                    <Input
                      className="pii-encrypt"
                      width="100%"
                      id="street2"
                      value={values.street2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Address line 2 (optional)"
                      marginBottom={theme.space[1]}
                    />
                    {invalidStreet2 && (
                      <FormMessage invalid={invalidStreet2}>
                        {errors.street2}
                      </FormMessage>
                    )}
                    {entityType === entityTypeEnum.user && (
                      <>
                        <Input
                          className="pii-encrypt"
                          width="100%"
                          id="street3"
                          value={values.street3}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Address line 3 (optional)"
                        />
                        {invalidStreet3 && (
                          <FormMessage invalid={invalidStreet3}>
                            {errors.street3}
                          </FormMessage>
                        )}
                      </>
                    )}
                  </Box>
                  <Box marginBottom={theme.space[5]}>
                    <Label htmlFor="country" marginBottom={theme.space[1]}>
                      Country
                    </Label>
                    <ReactSelect
                      className="pii-encrypt"
                      id="country"
                      aria-label="Select a country"
                      value={{
                        value: values.countryCode,
                        label: countryListUtil.getLabel(values.countryCode),
                      }}
                      onBlur={() => setFieldTouched('countryCode', true)}
                      options={countryListUtil.countryList}
                      styles={{
                        control: (
                          provided: React.CSSProperties,
                          state: { isFocused: boolean }
                        ) => ({
                          ...provided,
                          borderColor: invalidCountry
                            ? theme.colors.reds[100]
                            : state.isFocused
                            ? theme.colors.blues[100]
                            : '#849583',
                          boxShadow: invalidCountry
                            ? `0 0 4px 0 ${theme.colors.reds[100]}`
                            : state.isFocused
                            ? `0 0 4px 0 ${theme.colors.blues[100]}`
                            : 'none',
                          '&:hover': {
                            borderColor: invalidCountry
                              ? theme.colors.reds[100]
                              : state.isFocused
                              ? theme.colors.blues[100]
                              : '#849583',
                          },

                          borderRadius: '3px',
                          fontSize: '14px',
                          lineHeight: '20px',
                          backgroundColor: '#ffffff',
                        }),
                      }}
                      onChange={async (selectedOption: {
                        label: string;
                        value: string;
                      }) => {
                        await Promise.all([
                          setFieldValue('country', selectedOption.label),
                          setFieldValue('countryCode', selectedOption.value),
                          // When country changes, unset state/province and force user to select it again
                          setFieldValue('province', ''),
                          setFieldValue('state', ''),

                          // Show error message for state/province
                          setFieldTouched('state', true),
                          setFieldTouched('province', true),
                        ]);
                      }}
                    />
                    {invalidCountry && (
                      <FormMessage invalid={invalidCountry}>
                        {errors.countryCode}
                      </FormMessage>
                    )}
                  </Box>
                  <Flex marginBottom={theme.space[3]} width="100%">
                    <Box flexGrow="1">
                      <Label htmlFor="city" marginBottom={theme.space[1]}>
                        City
                      </Label>
                      <Input
                        className="pii-encrypt"
                        id="city"
                        width="100%"
                        value={values.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="City"
                        invalid={invalidCity}
                      />
                      {invalidCity && (
                        <FormMessage invalid={invalidCity}>
                          {errors.city}
                        </FormMessage>
                      )}
                    </Box>
                    {values.countryCode === 'US' && stateSelect}
                    {values.countryCode === 'CA' && provinceSelect}
                  </Flex>
                  <Box marginBottom={theme.space[6]}>
                    <Label htmlFor="zipcode" marginBottom={theme.space[1]}>
                      {values.countryCode === 'CA' ? 'Postal Code' : 'Zip Code'}
                    </Label>
                    <Input
                      className="pii-encrypt"
                      width="100%"
                      id="zipcode"
                      value={values.zipcode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="12345"
                      invalid={invalidZipCode}
                    />
                    {invalidZipCode && (
                      <FormMessage invalid={invalidZipCode}>
                        {errors.zipcode}
                      </FormMessage>
                    )}
                  </Box>
                  <Box marginBottom={theme.space[3]}>
                    <Label htmlFor="phoneNumber" marginBottom={theme.space[1]}>
                      Phone
                    </Label>
                    <Input
                      className="pii-encrypt"
                      width="100%"
                      id="phoneNumber"
                      value={values.phoneNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="+1 (123) 456-7890"
                      invalid={invalidPhoneNumber}
                    />
                    {invalidPhoneNumber && (
                      <FormMessage invalid={invalidPhoneNumber}>
                        {errors.phoneNumber}
                      </FormMessage>
                    )}
                  </Box>
                  {entityType === entityTypeEnum.user && (
                    <Box marginBottom={theme.space[3]}>
                      <Label htmlFor="phoneExt" marginBottom={theme.space[1]}>
                        Phone Extension
                      </Label>
                      <Input
                        className="pii-encrypt"
                        width="100%"
                        id="phoneExt"
                        value={values.phoneExt}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={invalidPhoneExt}
                      />
                      {invalidPhoneExt && (
                        <FormMessage invalid={invalidPhoneExt}>
                          {errors.phoneExt}
                        </FormMessage>
                      )}
                    </Box>
                  )}
                  <Box marginBottom={theme.space[3]}>
                    <Label htmlFor="emailAddress" marginBottom={theme.space[1]}>
                      Email
                    </Label>
                    <Input
                      className="pii-encrypt"
                      width="100%"
                      id="emailAddress"
                      value={values.emailAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="email@example.com"
                      invalid={invalidEmailAdress}
                    />
                    {invalidEmailAdress && (
                      <FormMessage invalid={invalidEmailAdress}>
                        {errors.emailAddress}
                      </FormMessage>
                    )}
                  </Box>
                  <Flex height={theme.space[10]}>
                    {
                      // for Users, the first address cannot be deleted because it's the default one
                      // for Teams, all addresses can be deleted
                      ((entityType === entityTypeEnum.user && index !== 0) ||
                        entityType === entityTypeEnum.team) &&
                        isEdit && (
                          <Button
                            kind="text-red"
                            type="button"
                            onClick={onRemove}
                          >
                            Remove
                          </Button>
                        )
                    }
                    <Box marginLeft="auto">
                      <Button
                        type="button"
                        kind={theme.kind.primaryOutline}
                        mr={theme.space[2]}
                        onClick={onCancelClick}
                      >
                        Cancel
                      </Button>
                      <Button
                        kind="primary"
                        type="submit"
                        disabled={
                          (values.country === 'Other' &&
                            values.internationalVerification === false) ||
                          isSubmitting
                        }
                      >
                        Save
                      </Button>
                    </Box>
                  </Flex>
                  {requestFailed && (
                    <Toast
                      kind={theme.kind.danger}
                      marginTop={theme.space[3]}
                      isOpen={true}
                      hideClose={true}
                    >
                      Request failed
                    </Toast>
                  )}
                </Flex>
              </Box>
            </form>
          );
        }}
      </Formik>
    </ShippingAddressPanel>
  );
};

export default EditShippingAddressForm;
